import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from "@angular/core";
import { GridsterConfig, GridsterItem } from "angular-gridster2";
import { Subject } from "rxjs";
import { ReportTextEditFormDialog, ReportTextEditFormDialogData } from "../../report-dialogs/report-text-edit-form/report-text-edit-form.dialog";
import { Dialog } from "@core/material";
import { FormControl, FormGroup } from "@angular/forms";
import { PAGE_OPTION_DEFAULT, ReportComponentType } from "@core/data/types/advanced-report";
import { editModeCheck } from "../../report-view/report-state/advanced-report.state";

@Component({
    selector: 'pv-report-text',
    changeDetection: ChangeDetectionStrategy.Default,
    preserveWhitespaces: false,
    templateUrl: 'report-text-component.html',
    styleUrls: ['report-text-component.scss'],
})
export class ReportTextComponent implements OnChanges, OnDestroy {

    @Input()
    textControl: FormControl;

    @Input()
    configFormGroup: FormGroup;

    @Input()
    item: GridsterItem;

    @Input()
    editMode: boolean;

    @Input()
    gridsterConfig: GridsterConfig;

    @Output()
    delete = new EventEmitter<boolean>();

    @Output()
    duplicate = new EventEmitter<ReportComponentType>();

    layerIndex = 1;

    /** Quill emits empty content as '' or '<p></p>', so strip tags/entities before testing. */
    get isUnset(): boolean {
        const v = this.textControl?.value;
        if (!v) return true;
        const stripped = String(v).replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        return stripped.length === 0;
    }

    private _destroy$ = new Subject();

    constructor(
        private _dialog: Dialog,
    ) {}

    ngOnChanges() {
        this.layerIndex = this.item?.layerIndex ? this.item.layerIndex : 1;
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    moveToTop() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.maxLayerIndex,
        });
    }

    moveForward() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: this.gridsterConfig.baseLayerIndex,
        });
    }

    moveDown() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.baseLayerIndex,
        });
    }

    moveToBottom() {
        if (!editModeCheck(this.editMode)) return;

        this.configFormGroup.patchValue({
            layerIndex: -this.gridsterConfig.maxLayerIndex,
        });
    }

    edit() {
        if (!editModeCheck(this.editMode)) return;

        const data: ReportTextEditFormDialogData = {
            content: this.textControl.value,
        }

        this._dialog.open(ReportTextEditFormDialog, {data}).afterClosed()
            .subscribe((result) => {
                if (!result.status) return;
                this.textControl.setValue(result.data.content);
                this.textControl.markAsDirty();
            })
    }

    remove() {
        this._dialog.confirm(
            'Delete Component',
            `Are you sure you want to delete this component?`,
            'Delete',
            'Cancel',
            true
        )
        .afterClosed()
        .subscribe(result => {
            if (!result) return;

            this.delete.emit(true);
        })
    }

    makeDuplicate() {
        this.duplicate.emit(ReportComponentType.TEXT);
    }
}